import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../../services/api';
import { renderError } from '../../../helpers/errors';
import {
    listAutomations,
    createAutomation,
    updateAutomation,
    toggleAutomation,
    reorderAutomations,
    deleteAutomation,
} from '../../../services/automations';
import { ruleToText } from './RuleSentence';
import { TRIGGERS } from '../../../seeds/crm/automations';

// Mapa coluna -> selos, reutilizado pelo board (função pura, sem hook).
export const buildStageBadgeMap = (automations = []) => {
    const map = {};
    (automations || []).forEach((a) => {
        if (!a.is_active || !a.pipeline_stage_id) return;

        const push = (badge) => {
            if (!map[a.pipeline_stage_id]) map[a.pipeline_stage_id] = [];
            map[a.pipeline_stage_id].push(badge);
        };

        const hasConditions = Array.isArray(a.conditions) && a.conditions.length > 0;

        if (a.trigger === TRIGGERS.BUDGET_CREATED) {
            push({ key: a.id, icon: '📥', label: hasConditions ? 'Orçamentos (regra)' : 'Novos orçamentos', tooltip: ruleToText(a) });
        } else if (a.trigger === TRIGGERS.ORDER_CREATED) {
            push({ key: a.id, icon: '🧾', label: hasConditions ? 'Pedidos (regra)' : 'Novos pedidos', tooltip: ruleToText(a) });
        } else if (a.trigger === TRIGGERS.CARD_ENTERED_STAGE) {
            push({ key: a.id, icon: '⚙️', label: 'Cria tarefa', tooltip: ruleToText(a) });
        }
    });
    return map;
};

export const useAutomations = (pipelineId = null) => {
    const [automations, setAutomations] = useState([]);
    const [pipelines, setPipelines] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(() => {
        setLoading(true);
        const params = pipelineId ? { pipeline_id: pipelineId } : {};
        return Promise.all([listAutomations(params), api.get('/pipeline')])
            .then(([autoRes, pipeRes]) => {
                setAutomations(autoRes.data.resources ?? []);
                setPipelines(pipeRes.data.resources ?? []);
            })
            .catch(renderError)
            .finally(() => setLoading(false));
    }, [pipelineId]);

    useEffect(() => {
        load();
    }, [load]);

    const create = (body) =>
        createAutomation(body)
            .then((res) => {
                toast.success('Automação criada');
                return load().then(() => res.data.resource);
            })
            .catch((e) => {
                renderError(e);
                throw e;
            });

    const update = (id, body) =>
        updateAutomation(id, body)
            .then((res) => {
                toast.success('Automação atualizada');
                return load().then(() => res.data.resource);
            })
            .catch((e) => {
                renderError(e);
                throw e;
            });

    const remove = (id) =>
        deleteAutomation(id)
            .then(() => {
                toast.success('Automação excluída');
                setAutomations((prev) => prev.filter((a) => a.id !== id));
            })
            .catch(renderError);

    // Otimista com rollback.
    const toggle = (id) => {
        const flip = (list) => list.map((a) => (a.id === id ? { ...a, is_active: !a.is_active } : a));
        setAutomations(flip);
        return toggleAutomation(id).catch((e) => {
            renderError(e);
            setAutomations(flip);
        });
    };

    // Otimista; recarrega em caso de erro.
    const reorder = (trigger, orderedIds) => {
        setAutomations((prev) => {
            const byId = Object.fromEntries(prev.map((a) => [a.id, a]));
            const reordered = orderedIds
                .map((id, index) => (byId[id] ? { ...byId[id], priority: index } : null))
                .filter(Boolean);
            const others = prev.filter((a) => a.trigger !== trigger);
            return [...others, ...reordered];
        });
        return reorderAutomations(trigger, orderedIds).catch((e) => {
            renderError(e);
            load();
        });
    };

    return { automations, pipelines, loading, refresh: load, create, update, toggle, remove, reorder };
};

export default useAutomations;
